// I giorni di calendario nel fuso dell'azienda (piano M3, D2), in un posto solo: le finestre delle chiamate recenti
// (M2, `recent-calls.ts`) e gli estremi dei filtri di data (`filter-days.ts`) contano i giorni allo stesso modo. Un
// giorno è anno, mese e giorno del calendario gregoriano (proleptico); il suo primo istante dipende dal fuso.
const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const dateFormatters = new Map();
/** Il giorno di calendario di un istante nel fuso indicato. */
export function localDateOf(instant, timeZone) {
    let formatter = dateFormatters.get(timeZone);
    if (!formatter) {
        formatter = new Intl.DateTimeFormat("en-US", { timeZone, year: "numeric", month: "numeric", day: "numeric" });
        dateFormatters.set(timeZone, formatter);
    }
    const values = {};
    for (const part of formatter.formatToParts(instant)) {
        if (part.type === "year" || part.type === "month" || part.type === "day")
            values[part.type] = Number(part.value);
    }
    return { year: values.year ?? 0, month: values.month ?? 0, day: values.day ?? 0 };
}
/** Un numero che cresce con il giorno: si confrontano due giorni senza oggetti `Date`. */
export const dayKey = (date) => date.year * 10_000 + date.month * 100 + date.day;
/** Un numero con gli zeri davanti fino a `length` cifre (due, se non si dice). */
export function padNumber(value, length = 2) {
    return String(value).padStart(length, "0");
}
/** Il nome di un giorno, `AAAA-MM-GG`: due nomi si confrontano come testo e hanno lo stesso ordine del calendario. */
export function dayString(date) {
    return `${padNumber(date.year, 4)}-${padNumber(date.month)}-${padNumber(date.day)}`;
}
const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;
/**
 * Il giorno di un nome `AAAA-MM-GG`, il contrario di `dayString`. Una forma sbagliata, un giorno che manca o un giorno
 * che il calendario non ha (`2026-02-30`) è un `RangeError`: i filtri di data e i periodi dei report lo leggono così.
 */
export function parseDayString(day) {
    const match = typeof day === "string" ? ISO_DAY.exec(day) : null;
    if (!match)
        throw new RangeError("day must be an ISO day (YYYY-MM-DD)");
    const date = { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
    // Un giorno che il calendario non ha scivola in un altro: il 30 febbraio diventa il 2 marzo.
    const same = addLocalDays(date, 0);
    if (same.year !== date.year || same.month !== date.month || same.day !== date.day)
        throw new RangeError("day does not exist");
    return date;
}
/**
 * Il tempo di un istante, in millisecondi, oppure `RangeError` con `label` se non è una data valida. Non si usa
 * `instanceof Date`: un `Date` di un altro contesto (un iframe, un test in un altro ambiente) non lo supererebbe.
 */
export function requireInstant(value, label) {
    const time = typeof value?.getTime === "function" ? value.getTime() : Number.NaN;
    if (!Number.isFinite(time))
        throw new RangeError(`${label} must be a valid date-time`);
    return time;
}
/** Il messaggio di chi riceve un calendario senza fuso: il fuso è un parametro obbligatorio, senza ricadute su Roma. */
export const MISSING_TIME_ZONE = "timeZone is required: pass the organization time zone";
/** Il fuso dell'azienda, oppure `RangeError(MISSING_TIME_ZONE)` se manca o è vuoto; che sia un fuso che esiste lo dice il calendario. */
export function requireTimeZone(timeZone) {
    if (typeof timeZone !== "string" || timeZone.trim() === "")
        throw new RangeError(MISSING_TIME_ZONE);
    return timeZone;
}
/**
 * Il primo istante del giorno `date` nel fuso indicato: la sua mezzanotte, oppure, se quella mezzanotte non esiste, il
 * primo minuto che in quel fuso è già quel giorno. Nessun fuso è avanti più di 14 ore né indietro più di 12, quindi la
 * mezzanotte cade fra 15 ore prima e 13 ore dopo la mezzanotte del giorno in UTC: lì una ricerca a metà, minuto per
 * minuto (gli scarti dei fusi di oggi sono minuti interi), trova il primo minuto di quel giorno in una dozzina di passi.
 */
export function startOfLocalDay(date, timeZone) {
    const target = dayKey(date);
    const midnightAsUtc = Date.UTC(date.year, date.month - 1, date.day);
    let before = midnightAsUtc - 15 * HOUR_MS; // ancora il giorno prima, in ogni fuso
    let from = midnightAsUtc + 13 * HOUR_MS; // già quel giorno o dopo, in ogni fuso
    while (from - before > MINUTE_MS) {
        const middle = before + Math.floor((from - before) / MINUTE_MS / 2) * MINUTE_MS;
        if (dayKey(localDateOf(middle, timeZone)) >= target)
            from = middle;
        else
            before = middle;
    }
    return new Date(from);
}
/**
 * Il giorno `days` giorni dopo `date` (prima, se `days` è negativo), sul calendario e non sulle ore: attraversa la
 * fine del mese e dell'anno e non sente i cambi dell'ora. `setUTCFullYear` e non `Date.UTC`, che leggerebbe gli anni da
 * 0 a 99 come 1900–1999.
 */
export function addLocalDays(date, days) {
    const utc = new Date(0);
    utc.setUTCFullYear(date.year, date.month - 1, date.day + days);
    return { year: utc.getUTCFullYear(), month: utc.getUTCMonth() + 1, day: utc.getUTCDate() };
}
//# sourceMappingURL=local-days.js.map